// frontend/src/main.tsx
// Phase 2 / TEN-03: read x-tenant-id meta tag (injected by the render route), validate, pass to embed harness.
// Phase 6 / OPS-02: Sentry.init runs BEFORE ReactDOM.createRoot; tenant_id tagged from meta tag.

import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import "./styles/light-theme.css";
import { startEmbedHarness } from "./lib/embed";

// --- Sentry init (must run before any other module-level work that may throw) ---------------------
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? "",
  environment: import.meta.env.VITE_ENV ?? "development",
  tracesSampleRate: 0, // No performance tracing in iframe-embed context
  beforeSend(event) {
    // PII scrubbing hook — current platform does not put lead PII in breadcrumbs, but this is the
    // canonical place to strip user.email / user.username if that ever changes.
    return event;
  },
});

const SLUG_RE = /^[a-z][a-z0-9-]{2,40}[a-z0-9]$/;
const RESERVED_SLUGS = new Set([
  "admin", "api", "app", "www",
  "docs", "dev", "staging", "test", "preview",
]);

export function parseTenantSlug(search: string): { slug: string; valid: boolean } {
  const params = new URLSearchParams(search);
  const raw = (params.get("t") ?? "").toLowerCase();
  if (!raw) return { slug: "", valid: false };
  const valid = SLUG_RE.test(raw) && !RESERVED_SLUGS.has(raw);
  return { slug: raw, valid };
}

function renderTenantErrorPage(message?: string): void {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML = `
    <div style="font-family:system-ui;padding:2rem;color:#1a1a1a">
      <h1>Widget nicht verfügbar</h1>
      <p>${message ?? "Tenant-Konfiguration fehlt. Bitte den Betreiber kontaktieren."}</p>
    </div>
  `;
}

// Read tenant identity from the meta tags injected by the serverless render route
// (backend/api/render/index.ts — Phase 08.2). Production requests always traverse render and
// arrive with x-tenant-id + x-tenant-slug meta tags pre-rendered.
const tenantIdMeta = document.querySelector('meta[name="x-tenant-id"]');
const tenantIdFromMeta = tenantIdMeta?.getAttribute("content") ?? null;

const effectiveTenantId = tenantIdFromMeta;

// Always tag Sentry — even when tenant is unresolved — so error events from the error-page
// branch are still attributable.
Sentry.setTag("tenant_id", effectiveTenantId ?? "unknown");

if (!effectiveTenantId) {
  renderTenantErrorPage("Tenant-Konfiguration fehlt. Bitte den Betreiber kontaktieren.");
} else {
  // Slug from the render-route-injected meta tag, fallback to URL ?t= param for direct test paths.
  const slugMeta = document.querySelector('meta[name="x-tenant-slug"]');
  const slugFromMeta = slugMeta?.getAttribute("content") ?? "";

  const slugSource = slugFromMeta
    ? `?t=${slugFromMeta}`
    : window.location.search;

  const { slug, valid } = parseTenantSlug(slugSource);

  if (!valid) {
    renderTenantErrorPage(
      slug
        ? "Ungültiger Tenant-Slug."
        : "Tenant erforderlich. URL muss `?t=<slug>` enthalten.",
    );
  } else {
    startEmbedHarness({ tenantId: effectiveTenantId, tenantSlug: slug });

    const rootEl = document.getElementById("root");
    if (rootEl) {
      ReactDOM.createRoot(rootEl).render(
        <React.StrictMode>
          <App />
        </React.StrictMode>,
      );
    }
  }
}
